<template>
  <div class="login-container">

    <div class="login-box">
      <div class="logo-container">
        <img src="@/assets/img/logo.jpg" alt="LaplaceMate" class="logo-img" />
      </div>
      <h1 class="login-title">{{ $t('login.title') }}</h1>
      <!-- 登录方式切换按钮 -->
      <div class="login-tab">
        <button
            class="tab-btn"
            :class="{ active: loginType === 'code' }"
            @click="loginType = 'code'"
        >
          {{ $t('login.codeLogin') }}
        </button>
        <button
            class="tab-btn"
            :class="{ active: loginType === 'password' }"
            @click="loginType = 'password'"
        >
          {{ $t('login.passwordLogin') }}
        </button>
      </div>

      <el-form
          ref="loginForm"
          :model="loginForm"
          :rules="loginRules"
          class="login-form"
          @keyup.enter.native="handleLogin"
      >
        <!-- 根据语言显示：中文显示手机号，英文显示邮箱 -->
        <el-form-item v-if="isChinese" prop="phone">
          <el-input
              v-model="loginForm.phone"
              :placeholder="$t('login.phonePlaceholder')"
              prefix-icon="el-icon-phone"
              maxlength="11"
              @input="formatPhone"
          ></el-input>
        </el-form-item>
        <el-form-item v-else prop="email">
          <el-input
              v-model="loginForm.email"
              :placeholder="$t('login.emailPlaceholder')"
              prefix-icon="el-icon-message"
              @input="formatEmail"
          ></el-input>
        </el-form-item>

        <!-- 动态显示：验证码输入（验证码登录）/ 密码输入（密码登录） -->
        <template v-if="loginType === 'code'">
          <!-- 验证码输入框 + 获取验证码按钮 -->
          <el-form-item prop="code">
            <el-row :gutter="10">
              <el-col :span="16">
                <el-input
                    v-model="loginForm.code"
                    :placeholder="$t('login.codePlaceholder')"
                    prefix-icon="el-icon-key"
                    maxlength="6"
                    @input="formatCode"
                ></el-input>
              </el-col>
              <el-col :span="8">
                <el-button
                    type="primary"
                    class="code-btn"
                    :disabled="countdown > 0 || !isInputValid"
                    @click="getVerificationCode"
                >
                  {{ countdown > 0 ? `${countdown}${$t('login.resendAfter')}` : $t('login.getCode') }}
                </el-button>
              </el-col>
            </el-row>
          </el-form-item>
        </template>

        <template v-else>
          <!-- 密码输入框 -->
          <el-form-item prop="password">
            <el-input
                v-model="loginForm.password"
                type="password"
                :placeholder="$t('login.passwordPlaceholder')"
                prefix-icon="el-icon-lock"
                show-password
            ></el-input>
          </el-form-item>
        </template>

        <!-- 登录按钮 -->
        <el-form-item>
          <el-button
              type="primary"
              class="login-btn"
              :loading="loading"
              @click="handleLogin"
          >
            {{ $t('login.login') }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginView',
  data() {
    return {
      loginType: 'code',
      loginForm: {
        phone: '',
        email: '',
        code: '',
        password: ''
      },
      loading: false,
      countdown: 0,
      timer: null
    }
  },
  computed: {
    // 判断是否为中文
    isChinese() {
      return this.$store.state.locale === 'zh'
    },
    // 检查手机号是否符合格式（控制获取验证码按钮状态）
    isPhoneValid() {
      const phoneReg = /^1[3-9]\d{9}$/
      return phoneReg.test(this.loginForm.phone)
    },
    // 检查邮箱是否符合格式（控制获取验证码按钮状态）
    isEmailValid() {
      const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailReg.test(this.loginForm.email)
    },
    // 根据语言返回对应的验证状态
    isInputValid() {
      return this.isChinese ? this.isPhoneValid : this.isEmailValid
    },
    // 动态验证规则
    loginRules() {
      const rules = {
        // 验证码验证规则（仅验证码登录生效）
        code: [
          {required: true, message: this.$t('login.codeRequired'), trigger: 'blur'},
          {pattern: /^\d{6}$/, message: this.$t('login.codeFormatError'), trigger: 'blur'}
        ],
        // 密码验证规则（仅密码登录生效）
        password: [
          {required: true, message: this.$t('login.passwordRequired'), trigger: 'blur'},
          {min: 6, max: 20, message: this.$t('login.passwordLengthError'), trigger: 'blur'}
        ]
      }

      // 根据语言添加不同的验证规则
      if (this.isChinese) {
        // 中文：手机号验证
        rules.phone = [
          {required: true, message: this.$t('login.phoneRequired'), trigger: 'blur'},
          {pattern: /^1[3-9]\d{9}$/, message: this.$t('login.phoneFormatError'), trigger: 'blur'}
        ]
      } else {
        // 英文：邮箱验证
        rules.email = [
          {required: true, message: this.$t('login.emailRequired'), trigger: 'blur'},
          {type: 'email', message: this.$t('login.emailFormatError'), trigger: 'blur'}
        ]
      }

      return rules
    }
  },
  beforeDestroy() {
    // 组件销毁前清除定时器
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    // 格式化手机号输入（只允许数字）
    formatPhone() {
      this.loginForm.phone = this.loginForm.phone.replace(/\D/g, '')
    },

    // 格式化邮箱输入（移除空格）
    formatEmail() {
      this.loginForm.email = this.loginForm.email.replace(/\s/g, '')
    },

    // 格式化验证码输入（只允许数字）
    formatCode() {
      this.loginForm.code = this.loginForm.code.replace(/\D/g, '')
    },

    // 获取验证码（仅验证码登录时调用）
    getVerificationCode() {
      const fieldName = this.isChinese ? 'phone' : 'email'
      // 先验证输入格式
      this.$refs.loginForm.validateField(fieldName, (error) => {
        if (!error) {
          // 格式正确，发起获取验证码请求
          const params = this.isChinese
            ? { phone: this.loginForm.phone }
            : { email: this.loginForm.email }
          this.$api.auth.getVerificationCode(params).then(res => {
            if (res.statusCode === 100200) {
              this.$message.success(this.$t('login.codeSent'))
              // 开始倒计时（60秒）
              this.startCountdown()
            } else {
              this.$message.error(this.$t('login.getCodeError'))
            }
          }).catch(err => {
            console.error('获取验证码出错:', err)
            // 如果是业务错误，使用国际化提示；如果是网络错误，使用网络错误提示
            if (err.isBusinessError) {
              this.$message.error(this.$t('login.getCodeError'))
            } else {
              this.$message.error(this.$t('login.networkError'))
            }
          })
        }
      })
    },

    // 倒计时功能
    startCountdown() {
      this.countdown = 60
      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
        } else {
          clearInterval(this.timer)
          this.timer = null
        }
      }, 1000)
    },

    // 处理登录（根据登录类型调用不同接口）
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loading = true
          // 根据语言构建请求参数
          const baseParams = this.isChinese
            ? { phone: this.loginForm.phone }
            : { email: this.loginForm.email }

          // 区分登录类型：验证码登录 / 密码登录
          if (this.loginType === 'code') {
            // 验证码登录接口
            this.$api.auth.loginByCode({
              ...baseParams,
              code: this.loginForm.code
            }).then(res => {
              this.handleLoginSuccess(res)
            }).catch(err => {
              this.handleLoginError(err)
            }).finally(() => {
              this.loading = false
            })
          } else {
            // 密码登录接口
            this.$api.auth.loginByPassword({
              ...baseParams,
              password: this.loginForm.password
            }).then(res => {
              this.handleLoginSuccess(res)
            }).catch(err => {
              this.handleLoginError(err)
            }).finally(() => {
              this.loading = false
            })
          }
        } else {
          return false
        }
      })
    },

    // 登录成功统一处理
    handleLoginSuccess(res) {
      console.log(res)
      if (res.statusCode === 100200) {
        this.$store.commit("setUser", res.data)
        this.$store.commit("setUserId", res.data.id ? String(res.data.id) : '')
        this.$store.commit("setIsAdmin", false)
        this.$message.success(this.$t('login.loginSuccess'))
        this.$router.push('/home')
        // 重置表单（避免切换登录方式时残留数据）
        this.$refs.loginForm.resetFields()
      } else {
        // 统一使用国际化提示，根据登录类型显示对应错误
        const errorMsg = this.loginType === 'code'
            ? this.$t('login.codeError')
            : (this.isChinese ? this.$t('login.loginError') : this.$t('login.loginErrorEmail'))
        this.$message.error(errorMsg)
      }
    },

    // 登录错误统一处理
    handleLoginError(err) {
      console.error('登录出错:', err)
      // 如果是业务错误，根据登录类型显示对应错误；如果是网络错误，使用网络错误提示
      if (err.isBusinessError) {
        const errorMsg = this.loginType === 'code'
            ? this.$t('login.codeError')
            : (this.isChinese ? this.$t('login.loginError') : this.$t('login.loginErrorEmail'))
        this.$message.error(errorMsg)
      } else {
        this.$message.error(this.$t('login.networkError'))
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  background-size: cover;
  background-image: url("@/assets/index/bc.jpg");
  background-position: center;
  position: relative;
}



.login-box {
  width: 90%;
  max-width: 400px;
  padding: 30px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.logo-img {
  max-width: 200px;
  height: auto;
  object-fit: contain;
}

.login-title {
  margin-bottom: 20px;
  text-align: center;
  color: #333;
  font-size: 24px;
}

/* 登录方式切换标签 */
.login-tab {
  display: flex;
  margin-bottom: 25px;
  border-bottom: 1px solid #e4e7ed;
}

.tab-btn {
  flex: 1;
  padding: 8px 0;
  background: transparent;
  border: none;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
}

.tab-btn.active {
  color: #409EFF;
  border-bottom: 2px solid #409EFF;
  font-weight: 500;
}

.tab-btn:hover:not(.active) {
  color: #409EFF;
}

.login-form {
  margin-bottom: 20px;
}

.login-btn, .code-btn {
  width: 100%;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .login-box {
    width: 85%;
    padding: 20px;
  }

  .login-title {
    font-size: 20px;
    margin-bottom: 15px;
  }

  .login-tab {
    margin-bottom: 20px;
  }

  .tab-btn {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .login-box {
    width: 90%;
    padding: 15px;
  }

  .login-title {
    font-size: 18px;
  }
}
</style>
